import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../auth/AuthContext";
import { ImageField } from "../../../components/admin/ImageField";

const EMPTY = { title: "", slug: "", excerpt: "", content: "", image_url: "", published: true };
const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-3 py-2.5 text-sm text-[#404040] focus:outline-none focus:border-[#B09060]";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-[#8F7648] mb-1";

function slugify(s) {
  return s.toLowerCase()
    .replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ş/g, "s")
    .replace(/ı/g, "i").replace(/ö/g, "o").replace(/ç/g, "c")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

export default function BlogSection() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(`Yazılar yüklenemedi: ${error.message}`);
    else setPosts(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    const row = {
      title: editing.title,
      slug: editing.slug || slugify(editing.title),
      excerpt: editing.excerpt || null,
      content: editing.content || null,
      image_url: editing.image_url || null,
      published: editing.published,
    };
    const { error } = editing.id
      ? await supabase.from("blog_posts").update(row).eq("id", editing.id)
      : await supabase.from("blog_posts").insert(row);
    setSaving(false);
    if (error) toast.error(`Kaydedilemedi: ${error.message}`);
    else {
      toast.success(editing.id ? "Yazı güncellendi." : "Yazı eklendi.");
      setEditing(null);
      load();
    }
  }

  async function remove(p) {
    if (!window.confirm(`"${p.title}" silinsin mi?`)) return;
    const { error } = await supabase.from("blog_posts").delete().eq("id", p.id);
    if (error) toast.error(error.message);
    else {
      toast.success("Yazı silindi.");
      setPosts((ps) => ps.filter((x) => x.id !== p.id));
    }
  }

  if (loading) return <div className="text-[#6B6B6B] text-sm py-10" data-testid="blog-loading">Yükleniyor...</div>;

  return (
    <div data-testid="blog-section">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-[#404040]">Blog Yönetimi</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">{posts.length} yazı</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="blog-add-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#B09060] text-white text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Yazı
        </button>
      </div>

      <div className="space-y-3 mt-6">
        {posts.map((p) => (
          <div key={p.id} data-testid={`blog-row-${p.slug}`} className="bg-white border border-[#B09060]/20 rounded-sm p-4 flex items-center gap-4 shadow-[0_4px_20px_rgba(176,144,96,0.05)]">
            {p.image_url && <img src={p.image_url} alt="" className="w-24 h-16 object-cover rounded-sm" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[#404040] text-sm truncate">{p.title}</div>
              <div className="text-[10px] uppercase tracking-[0.15em] mt-1">
                <span className={p.published ? "text-emerald-600 font-semibold" : "text-[#9B9B8F]"}>
                  {p.published ? "Yayında" : "Taslak"}
                </span>
                <span className="text-[#9B9B8F]"> · {new Date(p.created_at).toLocaleDateString("tr-TR")}</span>
              </div>
            </div>
            <button
              onClick={() => setEditing({ ...p })}
              data-testid={`blog-edit-${p.slug}`}
              className="p-2 rounded-sm border border-[#B09060]/30 text-[#8F7648] hover:bg-[#F2F3EB] transition-colors"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => remove(p)}
              data-testid={`blog-delete-${p.slug}`}
              className="p-2 rounded-sm border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="blog-edit-dialog">
          <form onSubmit={save} className="w-full max-w-2xl bg-white border border-[#B09060]/20 rounded-sm p-7 space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl text-[#404040]">{editing.id ? "Yazıyı Düzenle" : "Yeni Yazı"}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="blog-dialog-close" className="text-[#9B9B8F] hover:text-[#404040]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Başlık *</label>
                <input required data-testid="blog-form-title" className={inputCls} value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value, slug: editing.id ? editing.slug : slugify(e.target.value) })} />
              </div>
              <div>
                <label className={labelCls}>URL (slug)</label>
                <input className={inputCls} value={editing.slug}
                  onChange={(e) => setEditing({ ...editing, slug: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Özet</label>
                <input className={inputCls} value={editing.excerpt || ""}
                  onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>İçerik (paragraflar arasında boş satır bırakın)</label>
                <textarea rows={10} data-testid="blog-form-content" className={inputCls} value={editing.content || ""}
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Kapak Görseli</label>
                <ImageField
                  value={editing.image_url}
                  onChange={(url) => setEditing({ ...editing, image_url: url })}
                  userId={user?.id}
                  testid="blog-form-image"
                />
              </div>
              <label className="flex items-center gap-3 text-sm text-[#404040] cursor-pointer">
                <input
                  type="checkbox"
                  checked={editing.published}
                  onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                  data-testid="blog-form-published"
                  className="w-4 h-4 accent-[#B09060]"
                />
                Yayınla
              </label>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                data-testid="blog-form-save"
                className="flex-1 py-3 rounded-sm bg-[#B09060] text-white font-semibold text-xs uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors disabled:opacity-50"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
              <button type="button" onClick={() => setEditing(null)}
                className="px-6 py-3 rounded-sm border border-[#B09060]/30 text-[#404040] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors">
                Vazgeç
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
